import * as Cesium from 'cesium';

/**
 * Handles cylinder editing interactions (orientation + translation) while an edit popup is active.
 * - Left drag: translate cylinder in plane perpendicular to camera direction
 * - Right drag: rotate cylinder orientation (camera-relative yaw/pitch)
 */
export const createCylinderEditController = (viewer, {
  getEntityByPopupId,
  computeAxisFromOrientation,
  applyPopupUpdates,
  setEditingPopupId
}) => {
  const state = {
    handler: null,
    cameraControls: null,
    prevCursor: null,
    editingId: null,
    mode: null, // 'translate' | 'rotate' | null
    startMouse: null,
    startPosition: null,
    startOrientation: null,
    targetPosition: null,
    targetOrientation: null,
    dragPlane: null,
    startHit: null,
    dragCamUp: null,
    dragCamRight: null
  };

  const scratchRay = new Cesium.Ray();
  const scratchHit = new Cesium.Cartesian3();
  const scratchDelta = new Cesium.Cartesian3();

  const restoreCursor = () => {
    if (state.mode) return;
    if (viewer?.canvas) {
      const nextCursor = state.prevCursor !== null ? state.prevCursor : 'default';
      viewer.canvas.style.cursor = nextCursor;
    }
    state.prevCursor = null;
  };

  const restoreCameraInputs = () => {
    if (state.mode) return;
    if (!viewer?.scene?.screenSpaceCameraController) return;
    const controller = viewer.scene.screenSpaceCameraController;
    if (state.cameraControls) {
      controller.enableRotate = state.cameraControls.enableRotate;
      controller.enableTranslate = state.cameraControls.enableTranslate;
      controller.enableZoom = state.cameraControls.enableZoom;
      controller.enableTilt = state.cameraControls.enableTilt;
      controller.enableLook = state.cameraControls.enableLook;
      state.cameraControls = null;
    }
  };

  const disableCameraInputs = () => {
    if (!viewer?.scene?.screenSpaceCameraController) return;
    const controller = viewer.scene.screenSpaceCameraController;
    if (!state.cameraControls) {
      state.cameraControls = {
        enableRotate: controller.enableRotate,
        enableTranslate: controller.enableTranslate,
        enableZoom: controller.enableZoom,
        enableTilt: controller.enableTilt,
        enableLook: controller.enableLook
      };
    }
    controller.enableRotate = false;
    controller.enableTranslate = false;
    controller.enableZoom = false;
    controller.enableTilt = false;
    controller.enableLook = false;
  };

  // Convert a world-space drag position to its base-frame equivalent by
  // applying the drag delta to the entity's stored basePosition. The dataset
  // ENU translation (if any) is unchanged by the drag, so the world-space
  // delta equals the base-space delta.
  const dragWorldToBase = (entity, worldPosition) => {
    const info = entity?.treeInfo;
    if (!info?.basePosition || !state.startPosition || !worldPosition) {
      return worldPosition;
    }
    const delta = Cesium.Cartesian3.subtract(
      worldPosition,
      state.startPosition,
      new Cesium.Cartesian3()
    );
    return Cesium.Cartesian3.add(info.basePosition, delta, new Cesium.Cartesian3());
  };

  const updatePopupPosition = (popupId, cartesian) => {
    if (!viewer || !cartesian) return;
    const entity = getEntityByPopupId(popupId);
    const basePosition = dragWorldToBase(entity, cartesian);
    const carto = Cesium.Cartographic.fromCartesian(basePosition);
    const lon = Cesium.Math.toDegrees(carto.longitude);
    const lat = Cesium.Math.toDegrees(carto.latitude);
    const elevation = carto.height;

    const screenPosition = viewer.scene.cartesianToCanvasCoordinates(
      cartesian,
      new Cesium.Cartesian2()
    );

    applyPopupUpdates(popupId, {
      lon,
      lat,
      elevation,
      worldPosition: Cesium.Cartesian3.clone(cartesian),
      screenPosition: screenPosition
        ? { x: screenPosition.x, y: screenPosition.y }
        : null
    });
  };

  const intersectWithPlane = (screenPosition, plane, result) => {
    if (!screenPosition || !plane) return undefined;
    const ray = viewer.camera.getPickRay(screenPosition, scratchRay);
    if (!ray) return undefined;
    const hit = Cesium.IntersectionTests.rayPlane(ray, plane, result);
    return hit || undefined;
  };

  const finalizeOrientation = () => {
    const editingId = state.editingId;
    if (!editingId || state.mode !== 'rotate') return;
    const entity = getEntityByPopupId(editingId);
    const finalOrientation = state.targetOrientation || state.startOrientation;
    if (entity && finalOrientation) {
      entity.orientation = finalOrientation;
      const axisVector = computeAxisFromOrientation(entity);
      if (axisVector) {
        const axis = { x: axisVector.x, y: axisVector.y, z: axisVector.z };
        if (entity.treeInfo) {
          entity.treeInfo.axis = axis;
          // Mirror onto baseDirection so the re-apply effect (triggered on
          // any future transform change) doesn't reset the orientation.
          entity.treeInfo.baseDirection = new Cesium.Cartesian3(axis.x, axis.y, axis.z);
          if (entity.treeInfo.properties && typeof entity.treeInfo.properties === 'object') {
            entity.treeInfo.properties.dx = axis.x;
            entity.treeInfo.properties.dy = axis.y;
            entity.treeInfo.properties.dz = axis.z;
          }
        }
        applyPopupUpdates(editingId, { axis });
      }
    }
    state.mode = null;
    state.startMouse = null;
    state.startOrientation = null;
    state.targetOrientation = null;
    state.dragCamUp = null;
    state.dragCamRight = null;
    restoreCameraInputs();
    restoreCursor();
  };

  const finalizeTranslation = () => {
    const editingId = state.editingId;
    if (!editingId || state.mode !== 'translate') return;
    const entity = getEntityByPopupId(editingId);
    const finalPos = state.targetPosition || state.startPosition;
    if (entity && finalPos) {
      entity.position = Cesium.Cartesian3.clone(finalPos);
      if (entity.treeInfo) {
        // Move basePosition by the same world-space delta so the dataset's
        // translation effect doesn't snap the cylinder back on the next
        // transform change.
        const newBase = dragWorldToBase(entity, finalPos);
        entity.treeInfo.basePosition = Cesium.Cartesian3.clone(newBase);
        const carto = Cesium.Cartographic.fromCartesian(newBase);
        const lon = Cesium.Math.toDegrees(carto.longitude);
        const lat = Cesium.Math.toDegrees(carto.latitude);
        const elevation = carto.height;
        entity.treeInfo.lon = lon;
        entity.treeInfo.lat = lat;
        entity.treeInfo.elevation = elevation;
        if (entity.treeInfo.properties && typeof entity.treeInfo.properties === 'object') {
          const props = entity.treeInfo.properties;
          props.center_lon = lon;
          props.center_lat = lat;
          props.center_z = elevation;

          if (Number.isFinite(props.center_x) && Number.isFinite(props.center_y) && state.startPosition && state.targetPosition) {
            const enu = Cesium.Transforms.eastNorthUpToFixedFrame(state.startPosition);
            const east = Cesium.Matrix4.getColumn(enu, 0, new Cesium.Cartesian3());
            const north = Cesium.Matrix4.getColumn(enu, 1, new Cesium.Cartesian3());
            const delta = Cesium.Cartesian3.subtract(state.targetPosition, state.startPosition, new Cesium.Cartesian3());
            const eastDelta = Cesium.Cartesian3.dot(delta, Cesium.Cartesian3.normalize(east, east));
            const northDelta = Cesium.Cartesian3.dot(delta, Cesium.Cartesian3.normalize(north, north));
            if (Number.isFinite(eastDelta)) props.center_x += eastDelta;
            if (Number.isFinite(northDelta)) props.center_y += northDelta;
          }
        }
      }
      updatePopupPosition(editingId, finalPos);
    }
    state.mode = null;
    state.startPosition = null;
    state.targetPosition = null;
    state.dragPlane = null;
    state.startHit = null;
    restoreCameraInputs();
    restoreCursor();
  };

  const destroy = () => {
    finalizeOrientation();
    finalizeTranslation();
    if (state.handler) {
      try {
        state.handler.destroy();
      } catch (error) {
        console.warn('Failed to destroy cylinder edit handler:', error);
      }
      state.handler = null;
    }
    state.editingId = null;
    restoreCursor();
    state.cameraControls = null;
  };

  const activate = (editingId) => {
    destroy();
    if (!viewer || !editingId) return;

    const entity = getEntityByPopupId(editingId);
    if (!entity) {
      setEditingPopupId?.(null);
      return;
    }

    state.editingId = editingId;
    const handler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);
    state.handler = handler;

    handler.setInputAction((click) => {
      const target = getEntityByPopupId(editingId);
      if (!target) return;
      const picked = viewer.scene.pick(click.position);
      if (!Cesium.defined(picked) || picked.id !== target) return;

      state.mode = 'translate';

      const resolvedPosition = target.position?.getValue
        ? target.position.getValue(viewer.clock?.currentTime)
        : target.position;
      if (!resolvedPosition) {
        state.mode = null;
        return;
      }

      state.startPosition = Cesium.Cartesian3.clone(resolvedPosition);
      state.targetPosition = Cesium.Cartesian3.clone(resolvedPosition);

      const planeNormal = Cesium.Cartesian3.normalize(
        viewer.camera.direction,
        new Cesium.Cartesian3()
      );
      if (!planeNormal || !Cesium.Cartesian3.magnitude(planeNormal)) {
        state.mode = null;
        state.startPosition = null;
        state.targetPosition = null;
        state.dragPlane = null;
        state.startHit = null;
        return;
      }

      state.dragPlane = Cesium.Plane.fromPointNormal(
        state.startPosition,
        planeNormal
      );
      state.startHit = intersectWithPlane(click.position, state.dragPlane, new Cesium.Cartesian3());

      target.position = new Cesium.CallbackProperty(
        () => state.targetPosition || state.startPosition,
        false
      );

      if (viewer.canvas) {
        state.prevCursor = viewer.canvas.style.cursor || null;
        viewer.canvas.style.cursor = 'grabbing';
      }
      disableCameraInputs();
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

    handler.setInputAction((movement) => {
      if (state.mode === 'translate') {
        if (!state.dragPlane || !state.startHit || !state.startPosition) return;

        const currHit = intersectWithPlane(movement.endPosition, state.dragPlane, scratchHit);
        if (!currHit) return;

        Cesium.Cartesian3.subtract(currHit, state.startHit, scratchDelta);
        state.targetPosition = Cesium.Cartesian3.add(
          state.startPosition,
          scratchDelta,
          new Cesium.Cartesian3()
        );
        updatePopupPosition(editingId, state.targetPosition);
        return;
      }

      if (state.mode === 'rotate') {
        const target = getEntityByPopupId(editingId);
        if (!target || !movement.endPosition || !state.startMouse || !state.startOrientation || !state.dragCamUp || !state.dragCamRight) {
          return;
        }

        const dx = movement.endPosition.x - state.startMouse.x;
        const dy = movement.endPosition.y - state.startMouse.y;
        const yaw = dx * 0.005;
        const pitch = dy * 0.005;

        const qYaw = Cesium.Quaternion.fromAxisAngle(state.dragCamUp, yaw);
        const qPitch = Cesium.Quaternion.fromAxisAngle(state.dragCamRight, pitch);
        const qDelta = Cesium.Quaternion.multiply(qPitch, qYaw, new Cesium.Quaternion());
        state.targetOrientation = Cesium.Quaternion.multiply(
          qDelta,
          state.startOrientation,
          new Cesium.Quaternion()
        );
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    handler.setInputAction(() => {
      finalizeTranslation();
    }, Cesium.ScreenSpaceEventType.LEFT_UP);

    handler.setInputAction((click) => {
      const target = getEntityByPopupId(editingId);
      if (!target) return;

      const picked = viewer.scene.pick(click.position);
      if (!Cesium.defined(picked) || picked.id !== target) return;

      // If translation was in progress, finish it before starting rotation
      if (state.mode === 'translate') {
        finalizeTranslation();
      }

      state.mode = 'rotate';
      state.startMouse = Cesium.Cartesian2.clone(click.position);
      state.dragCamUp = Cesium.Cartesian3.normalize(viewer.camera.up, new Cesium.Cartesian3());
      state.dragCamRight = Cesium.Cartesian3.normalize(viewer.camera.right, new Cesium.Cartesian3());

      const currentOrientation = target.orientation?.getValue
        ? target.orientation.getValue(viewer.clock?.currentTime)
        : target.orientation;
      const baseOrientation = Cesium.Quaternion.clone(
        currentOrientation || Cesium.Quaternion.IDENTITY
      );
      state.startOrientation = baseOrientation;
      state.targetOrientation = baseOrientation;

      target.orientation = new Cesium.CallbackProperty(
        () => state.targetOrientation || Cesium.Quaternion.IDENTITY,
        false
      );

      if (viewer.canvas) {
        state.prevCursor = viewer.canvas.style.cursor || null;
        viewer.canvas.style.cursor = 'grabbing';
      }
      disableCameraInputs();
    }, Cesium.ScreenSpaceEventType.RIGHT_DOWN);

    handler.setInputAction(() => {
      finalizeOrientation();
    }, Cesium.ScreenSpaceEventType.RIGHT_UP);
  };

  return { activate, destroy };
};
